import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { PrimeNG } from 'primeng/config';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NotificationDTO, System } from 'src/app/models';
import { utils } from 'src/app/shared';
import { NotificationService } from 'src/app/shared/services';
import { StorageManagerService } from 'src/app/shared/services/storage-manager.service';
import { environment } from 'src/environments/environment';
import { MyDataService } from './services/my-data.service';
import { LayoutService } from './shared/components/layout/layout.service';
import { RealtimeService } from './services/realtime.service';
import { TicketNotificationsService } from './services/ticket-notifications.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styles: [],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  subscriptions: Subscription[] = [];
  newVersionModalVisible: boolean = false;

  @ViewChild('ticketCenterHeaderNav') ticketCenterHeaderNav?: TemplateRef<any>;

  constructor(
    private notificationService: NotificationService,
    private router: Router,
    private layoutService: LayoutService,
    private myDataService: MyDataService,
    private primeng: PrimeNG,
    private socketService: RealtimeService,
    private ticketNotificationsService: TicketNotificationsService,
    private storageManager: StorageManagerService
  ) {}

  ngOnInit(): void {
    this.storageManager.runStartupCleanup();
    this.primeng.setTranslation({
      startsWith: 'Começa com',
      contains: 'Contém',
      notContains: 'Não contém',
      endsWith: 'Termina com',
      equals: 'Igual a',
      notEquals: 'Diferente de',
      noFilter: 'Sem filtro',
      lt: 'Menor que',
      lte: 'Menor ou igual a',
      gt: 'Maior que',
      gte: 'Maior ou igual a',
      is: 'É',
      isNot: 'Não é',
      before: 'Antes de',
      after: 'Depois de',
      dateIs: 'Data é',
      dateIsNot: 'Data não é',
      dateBefore: 'Data antes de',
      dateAfter: 'Data depois de',
      clear: 'Limpar',
      apply: 'Aplicar',
      matchAll: 'Corresponder a todos',
      matchAny: 'Corresponder a qualquer',
      addRule: 'Adicionar regra',
      removeRule: 'Remover regra',
      accept: 'Sim',
      reject: 'Não',
      choose: 'Escolher',
      upload: 'Enviar',
      cancel: 'Cancelar',
      dayNames: [
        'domingo',
        'segunda-feira',
        'terça-feira',
        'quarta-feira',
        'quinta-feira',
        'sexta-feira',
        'sábado',
      ],
      dayNamesShort: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'],
      dayNamesMin: ['Do', 'Se', 'Te', 'Qa', 'Qi', 'Se', 'Sa'],
      monthNames: [
        'janeiro',
        'fevereiro',
        'março',
        'abril',
        'maio',
        'junho',
        'julho',
        'agosto',
        'setembro',
        'outubro',
        'novembro',
        'dezembro',
      ],
      monthNamesShort: [
        'jan',
        'fev',
        'mar',
        'abr',
        'mai',
        'jun',
        'jul',
        'ago',
        'set',
        'out',
        'nov',
        'dez',
      ],
      today: 'Hoje',
      weekHeader: 'Sm',
      firstDayOfWeek: 0,
      emptyMessage: 'Nenhum resultado encontrado',
      emptyFilterMessage: 'Nenhum resultado encontrado',
      emptySearchMessage: 'Nenhum resultado encontrado',
      selectionMessage: '{0} item(ns) selecionado(s)',
      emptySelectionMessage: 'Nenhum item selecionado',
      chooseYear: 'Escolha o ano',
      chooseMonth: 'Escolha o mês',
      chooseDate: 'Escolha a data',
      dateFormat: 'dd/mm/yy',
    });

    utils.setSystem(environment.system);

    this.subscriptions.push(
      this.notificationService.notificationActionEventEmitter.subscribe(
        (notification: NotificationDTO) => {
          if (notification.action) {
            switch (notification.action) {
              case 'open_order': {
                if (notification.data?.order_id) {
                  this.router.navigate([`pedidos/ver/${notification.data?.order_id}`]);
                }

                break;
              }
            }
          }
        }
      )
    );
    this.myDataService.clearStore().subscribe(() => {});

    // Inscrever globalmente no canal pessoal do usuário para notificações push de chamados.
    // AppComponent é o lugar correto pois sempre está ativo, independente da rota.
    this.subscriptions.push(
      this.socketService.getUserChannelMessages().subscribe((payload: any) => {
        if (payload?.data) {
          payload = payload.data;
        }
        if (!payload?.ticket_id) return;

        const user = utils.getUser();
        if (!user) return;

        const bwUserCG = utils.getBwUserCompanyGroup(user);
        const correctUserId = bwUserCG?.user_id || user?.id;

        const ticketRef = payload.ticket_number
          ? String(payload.ticket_number).padStart(10, '0')
          : payload.ticket_id;

        const changedBy = payload.changed_by_user_name || 'Usuário';
        const changes = payload.changes || 'Chamado atualizado';

        this.ticketNotificationsService.addTicketNotification({
          ref: ticketRef,
          uuid: payload.ticket_id,
          title: `Alterado por ${changedBy}`,
          summary: changes,
          type: 'ticket',
          userId: correctUserId,
          icon: payload.urgent ? 'bi-exclamation-triangle-fill' : 'bi-ticket',
          iconColor: payload.urgent ? 'text-danger' : 'text-primary',
          iconTooltip: payload.urgent ? 'Chamado Urgente' : 'Chamado',
        });
      })
    );

    // Chamado #0000579137: a IA que o usuário disparou no chamado terminou (mesmo com o modal fechado).
    this.subscriptions.push(
      this.socketService.getAiRunFinishedMessages().subscribe((payload: any) => {
        if (!payload?.ticket_id) return;

        const user = utils.getUser();
        if (!user) return;

        // O canal do usuário não é por grupo: ignora aviso de outro grupo empresa.
        const eventGroup = payload.company_group_id;
        const currentGroup = (utils.getCompanyGroup() as any)?.id;
        if (
          eventGroup &&
          currentGroup &&
          String(eventGroup).toLowerCase() !== String(currentGroup).toLowerCase()
        ) {
          return;
        }

        const bwUserCG = utils.getBwUserCompanyGroup(user);
        const failed = payload.status !== 'completed';

        this.ticketNotificationsService.addTicketNotification({
          ref: payload.ticket_number
            ? String(payload.ticket_number).padStart(10, '0')
            : payload.ticket_id,
          uuid: payload.ticket_id,
          title: `IA: ${payload.pipeline_name ?? 'Análise'}`,
          summary: payload.message ?? 'Execução de IA finalizada',
          type: 'ticket',
          userId: bwUserCG?.user_id || user?.id,
          icon: failed ? 'bi-exclamation-triangle-fill' : 'bi-stars',
          iconColor: failed ? 'text-danger' : 'text-primary',
          iconTooltip: failed ? 'Execução de IA com falha' : 'Execução de IA concluída',
          metadata: { aiRunId: payload.run_id },
        });
      })
    );
  }

  ngAfterViewInit(): void {
    if (this.ticketCenterHeaderNav && utils.getSystem() == System.TICKET_CENTER) {
      this.layoutService.headerNavCustomTemplate = this.ticketCenterHeaderNav;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
